import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetch } from 'hooks/fetch';
import { forceRefreshAnalytics, getBankConnections, getCategories, getFeed, } from 'actions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectUserId } from 'reducers/selectors';
import { setPotSpace } from 'features/pots/actions';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { setSignInSpace } from 'login/actions/signinNavigation';
import { setTradingAccountSpace } from 'actions/invest';
import { muteErrorAndResultButThrows, useFetchOld } from 'hooks';
import { acceptSpaceInvite, cancelSpaceInvite, createNewSpace, deleteSpace, deleteSpaceMember, deleteSpaceMemberSuccess, deleteSpaceSuccess, editSpaceInfoSuccess, fetchSpacesAction, getInvitesForSpace, getMySpaces, getSpaceInfo, getSpaceInfoSuccess, getSpaceInviteDetails, invitePeopleToSpace, joinSpace, rejectSpaceInvite, sendSpaceInviteReminder, setAccountSpace, setSelectedSpace, } from '../actions';
import { apiEvents, CREATE_SPACE_SUCCESS } from '../constants';
import { selectDefaultSpace, selectSpaces } from '../reducers/selectors';
export const useSpaceInfo = (id, fetchOnMount = true) => {
    const spaceInfo = useAppSelector((store) => id ? store.spaces.spaceInfo[id] : undefined);
    const [fetch, busy] = useFetch();
    const dispatch = useAppDispatch();
    const spaceInfoFetch = useCallback(() => {
        if (!busy && id !== undefined && !spaceInfo?.space.isDefault) {
            fetch(getSpaceInfo(id))
                .then((result) => {
                dispatch(getSpaceInfoSuccess(result));
            })
                .catch(() => { });
        }
    }, [busy, fetch, id, spaceInfo]);
    // Call on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => (fetchOnMount ? spaceInfoFetch() : undefined), []);
    return [spaceInfo, busy, spaceInfoFetch];
};
export const useSpaceInvite = (inviteId, spaceId) => {
    const dispatch = useAppDispatch();
    const spaceInvite = useAppSelector((store) => store.spaces.spaceInviteInfo[inviteId]);
    const mySpaces = useAppSelector(selectSpaces);
    const navigation = useMainStackNavigation();
    const [, , busy, fetch] = useFetchOld(muteErrorAndResultButThrows);
    const onContinue = useCallback(() => {
        if (mySpaces.length > 1) {
            navigation.pop();
            navigation.replace('JoinedSpace', {
                space: spaceInvite?.space,
            });
            return true;
        }
        navigation.pop();
        navigation.navigateDeprecated('SpacesIntro', {
            onContinue: () => {
                navigation.pop();
                navigation.replace('JoinedSpace', {
                    space: spaceInvite?.space,
                });
                return true;
            },
        });
        return true;
    }, [mySpaces.length, navigation, spaceInvite?.space]);
    const spaceInviteFetch = useCallback(async () => {
        if (!busy) {
            try {
                await fetch(getSpaceInviteDetails(spaceId, inviteId));
            }
            catch {
                navigation.pop();
            }
        }
    }, [busy, inviteId, spaceId]);
    useEffect(() => {
        spaceInviteFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const acceptInvite = useCallback(async (inviteId) => {
        if (spaceInvite) {
            try {
                await fetch(acceptSpaceInvite(spaceId, inviteId));
                dispatch(fetchSpacesAction());
                dispatch(setSelectedSpace(spaceId));
                navigation.navigateDeprecated('Success', {
                    title: 'Whooray!',
                    subTitle: `You have joined the space named "${spaceInvite?.space?.name}"`,
                    buttonText: 'Continue',
                    onContinue,
                });
            }
            catch {
                navigation.pop();
            }
        }
    }, [onContinue, spaceId, spaceInvite]);
    const rejectInvite = useCallback(async (inviteId) => {
        try {
            await fetch(rejectSpaceInvite(spaceId, inviteId));
        }
        catch {
            // No op
        }
        finally {
            navigation.pop();
        }
    }, [spaceId]);
    return {
        spaceInvite,
        fetching: busy,
        fetch: spaceInviteFetch,
        acceptInvite,
        rejectInvite,
    };
};
export const useManageSpaceInvite = (spaceId, inviteId, spaceUserId, handle, onFinish) => {
    const [, , busy, fetch] = useFetchOld();
    const dispatch = useAppDispatch();
    const [customFetching, setCustomFetching] = useState({
        fetchingCreateInvite: false,
        fetchingSendReminder: false,
        fetchingCancelInvite: false,
    });
    const createInvite = useCallback(async () => {
        if (!busy) {
            setCustomFetching((prev) => ({ ...prev, fetchingCreateInvite: true }));
            try {
                await fetch(invitePeopleToSpace(spaceId, [handle]));
                dispatch(getInvitesForSpace(spaceId));
                onFinish?.();
            }
            catch {
                //
            }
            finally {
                setCustomFetching((prev) => ({
                    ...prev,
                    fetchingCreateInvite: false,
                }));
            }
        }
    }, [busy, handle, onFinish, setCustomFetching, spaceId]);
    const sendReminder = useCallback(async () => {
        if (!busy) {
            setCustomFetching((prev) => ({ ...prev, fetchingSendReminder: true }));
            try {
                await fetch(sendSpaceInviteReminder(spaceId, spaceUserId, inviteId));
            }
            catch {
                //
            }
            finally {
                setCustomFetching((prev) => ({
                    ...prev,
                    fetchingSendReminder: false,
                }));
            }
        }
    }, [busy, inviteId, setCustomFetching, spaceId, spaceUserId]);
    const cancelInvite = useCallback(async () => {
        if (!busy) {
            setCustomFetching((prev) => ({ ...prev, fetchingCancelInvite: true }));
            try {
                await fetch(cancelSpaceInvite(spaceId, inviteId));
                dispatch(getInvitesForSpace(spaceId));
                onFinish?.();
            }
            catch {
                //
            }
            finally {
                setCustomFetching((prev) => ({
                    ...prev,
                    fetchingCancelInvite: false,
                }));
            }
        }
    }, [busy, inviteId, setCustomFetching, spaceId, onFinish]);
    return {
        createInvite,
        sendReminder,
        cancelInvite,
        fetchingCreateInvite: customFetching.fetchingCreateInvite,
        fetchingSendReminder: customFetching.fetchingSendReminder,
        fetchingCancelInvite: customFetching.fetchingCancelInvite,
    };
};
export const useDeleteSpace = (id, onRemoved) => {
    const [fetch, busy] = useFetch();
    const dispatch = useAppDispatch();
    const defaultSpace = useAppSelector(selectDefaultSpace);
    const remove = useCallback(async () => {
        if (!busy) {
            await fetch(deleteSpace(id));
            dispatch(deleteSpaceSuccess(defaultSpace?.id ?? -1));
            onRemoved();
        }
    }, [busy, fetch, id, onRemoved, defaultSpace?.id]);
    return [busy, remove];
};
export const useSetAccountsSpace = (currentSpace, newSpaces, onPatched) => {
    const mounted = useRef(true);
    const myId = useAppSelector(selectUserId);
    const [createSpace, createSpaceBusy] = useFetch();
    const [patch] = useFetch();
    const dispatch = useAppDispatch();
    const [patching, setPatching] = useState(false);
    const setSpace = useCallback(async () => {
        if (mounted.current &&
            !patching &&
            !createSpaceBusy &&
            currentSpace &&
            newSpaces) {
            setPatching(true);
            const { id: currentId, name, type } = currentSpace;
            let actualSpace = currentSpace;
            if (currentId < 0) {
                try {
                    actualSpace = (await createSpace(createNewSpace(name, type))).space;
                    dispatch({ type: CREATE_SPACE_SUCCESS });
                    dispatch(fetchSpacesAction());
                }
                catch {
                    setPatching(false);
                    return;
                }
            }
            const callsToRun = newSpaces.flatMap(({ space, accounts }) => {
                const resolvedId = space.id === currentId && currentId < 0 ? actualSpace.id : space.id;
                const isInSpace = (spaceId) => spaceId != null ? spaceId === space.id : space.isDefault;
                return accounts
                    .filter(({ userId, spaceId }) => userId === myId && !isInSpace(spaceId))
                    .map((account) => {
                    if ('productId' in account) {
                        return patch(setPotSpace(String(account.id), resolvedId));
                    }
                    if ('isEmmaInvestmentAccount' in account) {
                        return patch(setTradingAccountSpace(resolvedId, account.id));
                    }
                    return patch(setAccountSpace(account.id, resolvedId));
                });
            });
            if (callsToRun.length && mounted.current) {
                try {
                    await Promise.all(callsToRun);
                    if (mounted.current)
                        onPatched?.(actualSpace);
                    dispatch(fetchSpacesAction());
                    dispatch(getBankConnections());
                    dispatch(forceRefreshAnalytics());
                    dispatch(getCategories());
                    dispatch(getFeed());
                    dispatch(editSpaceInfoSuccess(actualSpace.id));
                }
                catch {
                    // No op
                }
            }
            else if (mounted.current)
                onPatched?.(actualSpace);
            setPatching(false);
        }
    }, [
        patching,
        createSpaceBusy,
        currentSpace,
        newSpaces,
        createSpace,
        myId,
        patch,
        onPatched,
        dispatch,
    ]);
    useEffect(() => () => {
        mounted.current = false;
    }, []);
    return [patching || createSpaceBusy, setSpace];
};
export const useJoinSpace = (showOnboarding = true) => {
    const navigation = useMainStackNavigation();
    const dispatch = useAppDispatch();
    const mySpaces = useAppSelector(selectSpaces);
    const join = useCallback(async (accessCode, setFetching) => {
        if (!accessCode)
            return;
        setFetching(true);
        dispatch(setSignInSpace(undefined));
        const res = await dispatch(joinSpace(accessCode));
        setFetching(false);
        if (res?.type !== apiEvents.joinSpace[1]) {
            navigation.pop();
            return;
        }
        const { space } = res.payload;
        dispatch(getMySpaces());
        dispatch(getSpaceInfo(space.id));
        navigation.navigateDeprecated('Success', {
            title: 'Whooray!',
            subTitle: `You have joined the space named "${space.name}"`,
            onContinue: () => {
                // case where there is only 1 space (default space)
                if (mySpaces.length === 1 && showOnboarding) {
                    navigation.pop();
                    navigation.navigateDeprecated('SpacesIntro', {
                        onContinue: () => {
                            navigation.pop();
                            navigation.replace('JoinedSpace', {
                                space,
                            });
                            return true;
                        },
                    });
                    return true;
                }
                navigation.pop();
                navigation.replace('JoinedSpace', {
                    space,
                });
                return true;
            },
            buttonText: 'Continue',
        });
    }, [mySpaces.length, showOnboarding]);
    return join;
};
export const useDeleteSpaceMember = (id, onSuccess) => {
    const [fetch, busy] = useFetch();
    const dispatch = useAppDispatch();
    const remove = useCallback(async (userId) => {
        if (!busy) {
            await fetch(deleteSpaceMember(id, userId));
            dispatch(deleteSpaceMemberSuccess(id, userId));
            onSuccess();
        }
    }, [busy, fetch, id, onSuccess]);
    return [remove, busy];
};
